import { useCallStore } from '../store/callStore';
import { cleanupAfterCallEnd } from './callCleanup';
import { createCallEffects } from './callEffects';
import { endNativeAudioRuntimeCall, stopNativeIncomingRinging } from '../services/callRuntime';
import { sendCallConnected } from '../services/callHistorySync';
import { refreshPresenceForIdentity } from '../services/presence';
import type { CallEndInfo } from './callTypes';
import type { CallStateAction } from './callStateReducer';
import type { ActiveCall, IncomingCall } from '../store/callStore';

type CreateCallEndHandlerParams = {
  navigate: (to: string) => void;
  clearPendingCallAction: () => void;
  stopRingtone: () => void;
  stopOutgoingDialTone: () => void;
  endCallState: ReturnType<typeof useCallStore.getState>['endCall'];
  setLastCallEndInfo: ReturnType<typeof useCallStore.getState>['setLastCallEndInfo'];
  markCallEndedInHistory: (params: {
    callSessionId: string;
    activeCall?: ActiveCall | null;
    incomingCall?: IncomingCall | null;
    reason?: string;
    source?: CallEndInfo['source'];
    durationSeconds?: number;
  }) => void;
  getActiveCallDurationSeconds: () => number | undefined;
  consumePendingPostCallJoin: (callSessionId: string) => { inviteToken: string; serverUrl: string } | null;
  onLocalCallEnded?: (info?: CallEndInfo) => void;
};

export function createCallEndHandler(params: CreateCallEndHandlerParams) {
  const effects = createCallEffects({
    markCallEndedInHistory: params.markCallEndedInHistory,
    getActiveCallDurationSeconds: params.getActiveCallDurationSeconds,
    consumePendingPostCallJoin: params.consumePendingPostCallJoin,
    navigate: params.navigate
  });

  return (info?: CallEndInfo) => {
    const activeCall = useCallStore.getState().activeCall;
    const incomingCall = useCallStore.getState().incomingCall;
    const remoteIdentityId = (activeCall?.remoteIdentityId || incomingCall?.fromIdentityId || '').trim();
    const localIdentityId = (activeCall?.localIdentityId || incomingCall?.localIdentityId || '').trim();
    const serverUrl = activeCall?.serverUrl || incomingCall?.localServerUrl;
    cleanupAfterCallEnd({
      info,
      activeCall,
      incomingCall,
      clearPendingCallAction: params.clearPendingCallAction,
      setLastCallEndInfo: params.setLastCallEndInfo,
      effects,
      navigate: params.navigate,
      endNativeAudioRuntimeCall,
      endCallState: params.endCallState,
      stopRingtone: params.stopRingtone,
      stopOutgoingDialTone: params.stopOutgoingDialTone
    });
    // Stop native incoming ringing if call ended while still in incoming state
    // (e.g. answered_elsewhere — cleanupAfterCallEnd only handles active native calls)
    const incomingId = incomingCall?.callSessionId;
    if (incomingId && (!activeCall || activeCall.callSessionId === incomingId)) {
      stopNativeIncomingRinging(incomingId);
    }
    if (remoteIdentityId) {
      void refreshPresenceForIdentity(remoteIdentityId, {
        localIdentityId,
        serverUrl
      }).catch(() => {});
    }
    params.onLocalCallEnded?.(info);
  };
}

export function markCallAsActive(dispatchCallState: (action: CallStateAction) => void) {
  const activeCall = useCallStore.getState().activeCall;
  if (!activeCall || activeCall.status === 'active') return;
  const startedAt = activeCall.startedAt || new Date();

  dispatchCallState({
    type: 'set-active-call',
    call: {
      ...activeCall,
      status: 'active',
      connectionPhase: undefined,
      startedAt
    }
  });
  void sendCallConnected(activeCall.callSessionId, startedAt.getTime(), {
    localIdentityId: activeCall.localIdentityId,
    serverUrl: activeCall.serverUrl
  }).catch(() => {});
}
